import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Select
} from '@mui/material';
import {Add, Delete, Edit, Save} from '@mui/icons-material';
import SoftBox from '../../../components/SoftBox';
import SoftButton from '../../../components/SoftButton';
import SoftTypography from '../../../components/SoftTypography';
import childApiClient from '../../../api/children/childrenApiClient';
import {getGuardianById} from '../../../api/guardian/guardianApiClient';
import AddChildrenDialog from '../../children/components/AddChildrenDialog';
import PropTypes from 'prop-types';

const ChildManagementDialog = ({open, onClose, guardianId, guardianChildren = [], onUpdate}) => {

    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [editingRelationship, setEditingRelationship] = useState(null);
    const [addChildDialog, setAddChildDialog] = useState(false);
    const [selectedChildId, setSelectedChildId] = useState('');
    const [selectedRelationship, setSelectedRelationship] = useState('GUARDIAN');
    const [createNewChildDialog, setCreateNewChildDialog] = useState(false);
    const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
    const [childToDelete, setChildToDelete] = useState(null);
    const [guardianDetails, setGuardianDetails] = useState(null);

    const relationships = [
        {value: 'MOTHER', label: 'Mother'},
        {value: 'FATHER', label: 'Father'},
        {value: 'GUARDIAN', label: 'Guardian'},
        {value: 'OTHER', label: 'Other'}
    ];

    useEffect(() => {
        if (open) {
            loadChildren();
            loadGuardianDetails();
        }
    }, [open, guardianId]); // loadGuardianDetails is not stable, so removed from dependencies

    const loadChildren = async () => {
        try {
            setLoading(true);
            const response = await childApiClient.getAllChildren();
            const childrenData = response.data || response;
            setChildren(childrenData);
        } catch (error) {
            console.error('Error loading children:', error);
            setError('Failed to load children');
        } finally {
            setLoading(false);
        }
    };

    const loadGuardianDetails = async () => {
        try {
            if (guardianId) {
                const response = await getGuardianById(guardianId);
                const guardian = response.data || response;
                setGuardianDetails(guardian);
            }
        } catch (error) {
            console.error('Error loading guardian details:', error);
            // Don't set error for guardian details as it's not critical
        }
    };

    const handleDeleteClick = (childData) => {
        setChildToDelete(childData);
        setDeleteConfirmDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (!childToDelete) return;
        
        try {
            setLoading(true);
            setError(null);

            // Handle different data structures to get the correct child ID
            const childData = childToDelete.childResponse || childToDelete.child || childToDelete;
            const childId = childData?.childId || childData?.id || childToDelete.childId;
            
            await childApiClient.removeGuardianFromChild(childId, guardianId);

            // Reset dialog state first
            setDeleteConfirmDialog(false);
            setChildToDelete(null);

            // Use requestAnimationFrame to ensure DOM updates are complete
            if (onUpdate) {
                requestAnimationFrame(() => {
                    onUpdate();
                });
            }
        } catch (error) {
            console.error('Error removing child:', error);
            setError('Failed to remove child');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirmDialog(false);
        setChildToDelete(null);
    };

    const handleUpdateRelationship = async (childId, relationship) => {
        try {
            setLoading(true);
            await childApiClient.updateGuardianRelationship(childId, guardianId, {relationship});

            // Reset edit state first
            setEditMode(false);
            setEditingRelationship(null);

            // Use requestAnimationFrame to ensure DOM updates are complete
            if (onUpdate) {
                requestAnimationFrame(() => {
                    onUpdate();
                });
            }
        } catch (error) {
            console.error('Error updating relationship:', error);
            setError('Failed to update relationship');
        } finally {
            setLoading(false);
        }
    };

    const handleAddExistingChild = async () => {
        if (!selectedChildId) {
            setError('Please select a child');
            return;
        }

        try {
            setLoading(true);
            await childApiClient.addGuardianToChild(selectedChildId, {
                guardianId: parseInt(guardianId),
                relationship: selectedRelationship
            });

            // Reset form state first
            setAddChildDialog(false);
            setSelectedChildId('');
            setSelectedRelationship('GUARDIAN');

            // Use requestAnimationFrame to ensure DOM updates are complete
            if (onUpdate) {
                requestAnimationFrame(() => {
                    onUpdate();
                });
            }
        } catch (error) {
            console.error('Error adding child:', error);
            setError('Failed to add child');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNewChild = async (formData) => {
        try {
            setLoading(true);
            // Add the guardian relationship to the form data
            const guardianRelationship = {
                guardianId: parseInt(guardianId),
                relationship: selectedRelationship
            };
            formData.append("guardianRelationships", JSON.stringify([guardianRelationship]));

            await childApiClient.addChild(formData);

            // Reset dialog state first
            setCreateNewChildDialog(false);

            // Use requestAnimationFrame to ensure DOM updates are complete
            if (onUpdate) {
                requestAnimationFrame(() => {
                    onUpdate();
                });
            }
        } catch (error) {
            console.error('Error creating child:', error);
            setError('Failed to create child');
        } finally {
            setLoading(false);
        }
    };

    const getChildName = (childData) => {
        // First try to get from the child data itself
        const data = childData.childResponse || childData.child || childData;
        if (data && data.firstName && data.lastName) {
            return `${data.firstName} ${data.lastName}`;
        }

        // Fallback to looking up in children array by ID
        const childId = data?.childId || data?.id || childData.childId;
        const child = children.find(c => c.childId === childId);
        return child ? `${child.firstName} ${child.lastName}` : 'Unknown Child';
    };

    const getRelationshipLabel = (relationship) => {
        const rel = relationships.find(r => r.value === relationship);
        return rel ? rel.label : relationship;
    };

    const availableChildren = children.filter(child => {
        const isAlreadyAssigned = guardianChildren.some(gc => {
            const gcChildId = gc.childResponse?.childId || gc.childId;
            return gcChildId === child.childId || gcChildId === parseInt(child.childId);
        });
        return !isAlreadyAssigned;
    });



    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <SoftTypography variant="h6">Manage Children Relationships</SoftTypography>
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <SoftBox mb={2}>
                            <SoftTypography color="error">{error}</SoftTypography>
                        </SoftBox>
                    )}

                    <SoftBox mb={2} display="flex" gap={2} alignItems="center">
                        <SoftButton
                            variant="contained"
                            color="primary"
                            startIcon={<Add/>}
                            onClick={() => setAddChildDialog(true)}
                            disabled={loading || availableChildren.length === 0}
                        >
                            Add Existing Child
                        </SoftButton>
                        <SoftButton
                            variant="contained"
                            color="text"
                            startIcon={<Add/>}
                            onClick={() => setCreateNewChildDialog(true)}
                            disabled={loading}
                        >
                            Create New Child
                        </SoftButton>
                        {availableChildren.length === 0 && children.length > 0 && (
                            <SoftTypography variant="caption" color="text.secondary">
                                All available children are already assigned
                            </SoftTypography>
                        )}
                        {children.length === 0 && !loading && (
                            <SoftTypography variant="caption" color="text.secondary">
                                No children available in the system
                            </SoftTypography>
                        )}
                    </SoftBox>

                    {loading && guardianChildren.length === 0 ? (
                        <SoftBox textAlign="center" py={3}>
                            <SoftTypography variant="body2" color="textSecondary">
                                Loading children information...
                            </SoftTypography>
                        </SoftBox>
                    ) : (
                        <List>
                            {guardianChildren.map((child) => {
                                const childData = child.childResponse || child.child || child;
                                const childId = childData?.childId || childData?.id || child.childId;

                                return (
                                    <ListItem key={childId} divider>
                                        <ListItemText
                                            primary={getChildName(child)}
                                            secondary={`ID: ${childId}`}
                                        />
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                            {editMode && editingRelationship?.childId === childId ? (
                                                <FormControl size="small" sx={{minWidth: 120}}>
                                                    <Select
                                                        value={editingRelationship.relationship}
                                                        onChange={(e) => setEditingRelationship({
                                                            ...editingRelationship,
                                                            relationship: e.target.value
                                                        })}
                                                    >
                                                        {relationships.map((rel) => (
                                                            <MenuItem key={rel.value} value={rel.value}>
                                                                {rel.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            ) : (
                                                <Chip
                                                    label={getRelationshipLabel(child.relationship)}
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            )}

                                            <IconButton
                                                onClick={() => {
                                                    if (editMode && editingRelationship?.childId === childId) {
                                                        handleUpdateRelationship(childId, editingRelationship.relationship);
                                                    } else {
                                                        setEditMode(true);
                                                        setEditingRelationship({...child, childId});
                                                    }
                                                }}
                                                disabled={loading}
                                            >
                                                {editMode && editingRelationship?.childId === childId ? <Save/> :
                                                    <Edit/>}
                                            </IconButton>

                                            <IconButton
                                                onClick={() => handleDeleteClick(child)}
                                                disabled={loading}
                                                color="error"
                                            >
                                                <Delete/>
                                            </IconButton>
                                        </Box>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}

                    {!loading && guardianChildren.length === 0 && (
                        <SoftBox textAlign="center" py={3}>
                            <SoftTypography variant="body2" color="textSecondary">
                                No children assigned to this guardian
                            </SoftTypography>
                        </SoftBox>
                    )}

                </DialogContent>
                <DialogActions>
                    <SoftButton onClick={onClose} disabled={loading}>
                        Close
                    </SoftButton>
                </DialogActions>
            </Dialog>

            {/* Add Child Dialog */}
            <Dialog open={addChildDialog} onClose={() => setAddChildDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Existing Child to Guardian</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{mt: 1}}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Select Child</InputLabel>
                                <Select
                                    value={selectedChildId}
                                    onChange={(e) => setSelectedChildId(e.target.value)}
                                    label="Select Child"
                                    displayEmpty
                                >
                                    <MenuItem value="" disabled>
                                        <em>Please select a child</em>
                                    </MenuItem>
                                    {availableChildren.map((child) => (
                                        <MenuItem key={child.childId} value={child.childId}>
                                            {child.firstName} {child.lastName} (ID: {child.childId})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Relationship</InputLabel>
                                <Select
                                    value={selectedRelationship}
                                    onChange={(e) => setSelectedRelationship(e.target.value)}
                                    label="Relationship"
                                    displayEmpty
                                >
                                    <MenuItem value="" disabled>
                                        <em>Please select a relationship</em>
                                    </MenuItem>
                                    {relationships.map((rel) => (
                                        <MenuItem key={rel.value} value={rel.value}>
                                            {rel.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddChildDialog(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button onClick={handleAddExistingChild} variant="contained" disabled={loading}>
                        Add Child
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmDialog}
                onClose={handleCancelDelete}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <SoftTypography variant="h6" color="error">
                        Remove Child Relationship
                    </SoftTypography>
                </DialogTitle>
                <DialogContent>
                    <SoftTypography variant="body1">
                        Are you sure you want to remove{' '}
                        <strong>{childToDelete ? getChildName(childToDelete) : ''}</strong>{' '}
                        from this guardian&apos;s children relationships?
                    </SoftTypography>
                    <SoftBox mt={2}>
                        <SoftTypography variant="body2" color="textSecondary">
                            This action will permanently remove the guardian relationship.
                            The child will no longer be associated with this guardian.
                        </SoftTypography>
                    </SoftBox>
                </DialogContent>
                <DialogActions>
                    <SoftButton
                        onClick={handleCancelDelete}
                        disabled={loading}
                        color="text"
                    >
                        Cancel
                    </SoftButton>
                    <SoftButton
                        onClick={handleConfirmDelete} 
                        disabled={loading}
                        color="error"
                        variant="contained"
                    >
                        {loading ? 'Removing...' : 'Remove Child'}
                    </SoftButton>
                </DialogActions>
            </Dialog>

            {/* Create New Child Dialog */}
            <AddChildrenDialog
                open={createNewChildDialog}
                onClose={() => setCreateNewChildDialog(false)}
                onSubmit={handleCreateNewChild}
                isEdit={false}
                guardian={guardianDetails}
                preSelectedGuardian={guardianDetails}
                disableGuardianSelection={true}
                fromGuardianView={true}
            />
        </>
    );
};

ChildManagementDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    guardianId: PropTypes.number.isRequired,
    guardianChildren: PropTypes.arrayOf(
        PropTypes.shape({
            childId: PropTypes.number.isRequired,
            relationship: PropTypes.string.isRequired
        })
    ),
    onUpdate: PropTypes.func
};

export default ChildManagementDialog;
